import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { PostTimeField } from './PostTimeField';
import { calendarMessages } from './calendarMessages';
import type { PostStateActionsProps } from './dayPanelTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * A scheduled post (design b5): the time field (a move keeps it scheduled), "Edit in Composer"
 * and Cancel. A cancel gives the scheduled post back to this month. Delete comes after the
 * cancel (owner rule). A clip that is still being made shows "Making the clip".
 */
const ScheduledActionsComponent: React.FC<PostStateActionsProps> = ({ post, actions, onEditInComposer }) => {
  const intl = useIntl();
  const isMakingClip = post.status === 'rendering' || post.status === 'ready';

  const cancel = useCallback(() => {
    actions.cancel(post.uuid).catch(console.error);
  }, [actions, post.uuid]);
  const move = useCallback(
    (at: Date) => {
      actions.move(post.uuid, at).catch(console.error);
    },
    [actions, post.uuid]
  );

  return (
    <>
      <div className={styles.dpBody}>
        {isMakingClip ? <p className={styles.why}>{intl.formatMessage(calendarMessages.makingClip)}</p> : null}
        {post.status === 'scheduled' ? (
          <PostTimeField
            scheduledAt={post.scheduledAt}
            label={intl.formatMessage({ id: 'app.sml.promote.calendar.time.label', defaultMessage: 'Time' })}
            isSaving={actions.isWriting}
            onSave={move}
          />
        ) : null}
      </div>
      <div className={styles.dpActs}>
        <SMLButton size="sm" outline omitCap stretch onClick={() => onEditInComposer(post)}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.editInComposer', defaultMessage: 'Edit in Composer' })}
        </SMLButton>
        <button type="button" className={styles.linkbtn} disabled={actions.isWriting} onClick={cancel}>
          {intl.formatMessage({ id: 'app.sml.promote.post.cancelPost', defaultMessage: 'Cancel this post' })}
        </button>
        <span className={styles.tiny}>
          {intl.formatMessage({
            id: 'app.sml.promote.calendar.scheduled.cancelNote',
            defaultMessage: 'Canceling gives the scheduled post back to this month.',
          })}
        </span>
      </div>
    </>
  );
};

export const ScheduledActions = TypedMemo(ScheduledActionsComponent);
